import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { supabase } from '../../lib/supabase';

// Free-trial status strip shown above every page for trial organisations
// (organizations.trial_ends_at set). Limits are enforced by sync-lessonspace;
// this only tells the customer where they stand.
const TrialBanner = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [trial, setTrial] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setTrial(null);
    if (!organizationId) return undefined;
    (async () => {
      const { data: org } = await supabase
        .from('organizations')
        .select('trial_ends_at, trial_max_minutes, billing_status, default_payment_last4')
        .eq('id', organizationId)
        .maybeSingle();
      // Paying customers (card on file) don't see the trial strip.
      if (!org?.trial_ends_at || (org.billing_status === 'active' && org.default_payment_last4)) return;
      const { data: usedMinutes } = await supabase.rpc('org_lesson_minutes', { p_org: organizationId });
      if (!cancelled) setTrial({ endsAt: new Date(org.trial_ends_at), maxMinutes: org.trial_max_minutes, usedMinutes: Number(usedMinutes || 0) });
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  if (!trial) return null;

  const msLeft = trial.endsAt.getTime() - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / 86400000));
  const hoursUsed = Math.round(trial.usedMinutes / 60);
  const maxHours = trial.maxMinutes != null ? Math.round(trial.maxMinutes / 60) : null;
  const ended = msLeft <= 0 || (trial.maxMinutes != null && trial.usedMinutes >= trial.maxMinutes);

  const base = 'mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-4 py-2.5 text-sm';
  if (ended) {
    return (
      <div className={`${base} ${isDark ? 'border-amber-500/30 bg-amber-500/10 text-amber-200' : 'border-amber-300 bg-amber-50 text-amber-900'}`}>
        <Clock className="h-4 w-4 shrink-0" />
        <span>Your free trial has ended, so new lessons are no longer being imported. Everything analysed so far stays available.</span>
        <Link to="/billing" className="font-medium underline underline-offset-2">Add payment details to continue</Link>
        <a href="mailto:support@tutortracer.com?subject=Continuing%20with%20TutorTracer" className="underline underline-offset-2 opacity-80">or talk to us</a>
      </div>
    );
  }
  return (
    <div className={`${base} ${isDark ? 'border-[#3f3f46] bg-[#18181b] text-[#d4d4d8]' : 'border-[#e5e5e5] bg-white text-[#444]'}`}>
      <Sparkles className={`h-4 w-4 shrink-0 ${isDark ? 'text-[#a78bfa]' : 'text-[#7662ac]'}`} />
      <span className="font-medium">Free trial</span>
      <span>{daysLeft} {daysLeft === 1 ? 'day' : 'days'} left</span>
      {maxHours != null && <span className={isDark ? 'text-[#a1a1aa]' : 'text-[#777]'}>{hoursUsed} of {maxHours} lesson hours used</span>}
      <Link to="/billing" className="ml-auto text-xs font-medium underline underline-offset-2">Add payment details to keep going after the trial</Link>
    </div>
  );
};

export default TrialBanner;
